import { isInstant } from '../../../../shared/audit/filters-model'
import { REQUEST_LOG_MAX_MS } from '../../../../shared/audit/trace-links'
import { dateText, dayOf, timeText as clockText, viewerTimeZone, wallClock, zoneAbbr } from '../../../../shared/date/local-time'

// What the Request log shows, as its URL holds it, so a link or a bookmark
// opens the same view (the trace links in shared/audit/trace-links.js):
//
//   status, errors  a status class or code; only requests with an error
//   range           a preset (left out when it is the default)
//   session         one sign-in session's requests
//   request         one request id
//   trace           one flow's requests (a sign-in attempt: core's trace
//                   key, "signin:<hex>", never a secret)
//   ip              one client address, or a CIDR range
//   from, to        an exact window (UTC instants; to is exclusive)
//
// An exact window always wins over a preset. With a session, a request or a
// flow and no exact window, the list reads the last 31 days (the most the log
// reads at once) unless a preset is picked. The URL is input from anywhere,
// so filtersFromSearch keeps only what the server accepts and never throws.

const HOUR = 36e5
const DAY = 864e5

export const RANGES = [
  { value: '1h', label: 'Last hour', ms: HOUR },
  { value: '24h', label: 'Last 24 hours', ms: 24 * HOUR },
  { value: '7d', label: 'Last 7 days', ms: 7 * DAY }
]
// Only with a session or a request: their reads are indexed, so the whole
// span the server allows (content audit-request maxList).
const LONG_RANGE = { value: '31d', label: 'Last 31 days', ms: REQUEST_LOG_MAX_MS }

// content's maxRoutes: the slowest routes cover at most 7 days.
export const ROUTES_MAX_MS = 7 * DAY

// "Around this moment" and its widen choices, either side of the moment.
export const AROUND_CHOICES = [
  { ms: 10e3, label: '10 seconds' },
  { ms: 60e3, label: '1 minute' },
  { ms: 600e3, label: '10 minutes' }
]
const AROUND_MAX_MS = 2 * AROUND_CHOICES[AROUND_CHOICES.length - 1].ms

export const URL_KEYS = ['status', 'errors', 'range', 'session', 'request', 'trace', 'ip', 'from', 'to']

const STATUS = /^([1-5]xx|[1-5]\d\d)$/
const SESSION = /^[0-9A-Za-z-]{1,64}$/
const REQUEST = /^[0-9A-Za-z-]{8,64}$/
// core request.NoteTrace's key shape
const TRACE = /^[a-z][a-z0-9_]{0,31}:[A-Za-z0-9_-]{1,64}$/
// an IPv4 or IPv6 address, or a CIDR range (content checks it exactly)
const IP = /^[0-9A-Fa-f:.]{2,45}(\/\d{1,3})?$/
// An instant is kept exactly when it is already full (the server's own may
// end inside a millisecond); otherwise written out in full.
const FULL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3,9}Z$/
const instant = v => (FULL.test(v) ? v : new Date(v).toISOString())
const iso = ms => new Date(ms).toISOString()

// A session, a request or a flow: indexed reads, so the long range too. An
// address is not (the window is scanned), so it keeps the short ranges.
const scoped = f => Boolean(f && (f.session || f.request || f.trace))
export const rangesFor = f => (scoped(f) ? [...RANGES, LONG_RANGE] : RANGES)
export const defaultRange = f => (scoped(f) ? LONG_RANGE.value : '24h')
export const rangeOf = f => {
  const list = rangesFor(f)
  return list.find(r => r.value === (f.range || defaultRange(f))) || list.find(r => r.value === defaultRange(f))
}

export const isExact = f => Boolean(f && f.from && f.to)
export const spanOf = (from, to) => Date.parse(to) - Date.parse(from)

// The window the list reads: the exact one, or the preset's ending now.
export const windowFor = (f, now = new Date()) => {
  if (isExact(f)) return { from: f.from, to: f.to }
  return { from: iso(now.getTime() - rangeOf(f).ms), to: now.toISOString() }
}

export const filtersFromSearch = search => {
  const p = new URLSearchParams(search)
  const get = k => (p.get(k) || '').trim()
  const f = { status: '', errors: p.get('errors') === '1', range: '', session: '', request: '', trace: '', ip: '', from: '', to: '' }
  if (STATUS.test(get('status'))) f.status = get('status')
  if (SESSION.test(get('session'))) f.session = get('session')
  if (REQUEST.test(get('request'))) f.request = get('request')
  if (TRACE.test(get('trace'))) f.trace = get('trace')
  if (IP.test(get('ip'))) f.ip = get('ip')
  const from = get('from')
  const to = get('to')
  if (isInstant(from) && isInstant(to) && Date.parse(from) < Date.parse(to)) {
    f.to = instant(to)
    // longer than the log reads at once: keep the end, move the start in
    f.from = spanOf(from, to) > REQUEST_LOG_MAX_MS ? iso(Date.parse(f.to) - REQUEST_LOG_MAX_MS) : instant(from)
  }
  const range = get('range')
  if (!isExact(f) && range !== defaultRange(f) && rangesFor(f).some(r => r.value === range)) f.range = range
  return f
}

// The view back into a query string; keys the page doesn't own (the
// grids' search, the drawer's ?at=) are kept.
export const searchWithFilters = (search, f) => {
  const p = new URLSearchParams(search)
  const put = (k, v) => (v ? p.set(k, v) : p.delete(k))
  const exact = isExact(f)
  put('status', f.status)
  put('errors', f.errors ? '1' : '')
  put('range', exact ? '' : f.range)
  put('session', f.session)
  put('request', f.request)
  put('trace', f.trace)
  put('ip', f.ip)
  put('from', exact ? f.from : '')
  put('to', exact ? f.to : '')
  const s = p.toString()
  return s ? `?${s}` : ''
}

// A window in words, in the reader's zone (tz, default theirs): "Oct 6,
// 2026, 12:41:20–12:41:40 PM MDT" inside one day; "Sep 5, 12:41 PM – Oct 6,
// 2026, 12:41 PM MDT" across days (each end with its own zone name when
// they differ: a daylight-saving change between them). Seconds show when
// either end has them.
const clock = (d, seconds, tz) => clockText(d, { tz, seconds, zone: false })
const hasSeconds = d => Boolean(d.getUTCSeconds() || d.getUTCMilliseconds())
export const windowText = (from, to, tz = viewerTimeZone()) => {
  const a = new Date(from)
  const b = new Date(to)
  const seconds = [a, b].some(hasSeconds)
  const ta = clock(a, seconds, tz)
  const tb = clock(b, seconds, tz)
  const za = zoneAbbr(a, tz)
  const zb = zoneAbbr(b, tz)
  if (dayOf(a, tz) === dayOf(b, tz) && za === zb) {
    const start = ta.slice(-2) === tb.slice(-2) ? ta.slice(0, -3) : ta
    return `${dateText(b, { tz })}, ${start}–${tb} ${zb}`
  }
  const yearA = wallClock(a, tz).year !== wallClock(b, tz).year
  return `${dateText(a, { tz, year: yearA })}, ${ta}${za === zb ? '' : ` ${za}`} – ${dateText(b, { tz })}, ${tb} ${zb}`
}

// A moment's time alone, in the reader's zone ("12:41:30 PM MDT").
export const momentText = (at, tz = viewerTimeZone()) => clockText(at, { tz, seconds: true })

// One end of a window, with its day ("Sep 5, 2026, 12:41 PM MDT").
export const instantText = (v, tz = viewerTimeZone()) => {
  const d = new Date(v)
  return `${dateText(d, { tz })}, ${clockText(d, { tz, seconds: hasSeconds(d) })}`
}

// An exact window of 20 minutes or less is a moment and its reach either
// side (what "Around this moment" opened); anything else is null.
export const aroundOf = f => {
  if (!isExact(f)) return null
  const span = spanOf(f.from, f.to)
  if (!(span > 0) || span > AROUND_MAX_MS) return null
  return { at: iso(Date.parse(f.from) + span / 2), ms: span / 2 }
}
export const aroundWindow = (at, ms) => ({ from: iso(Date.parse(at) - ms), to: iso(Date.parse(at) + ms) })
